/**
 * Owner Portal Video Guides — a premium horizontal video carousel for the
 * Reports/Help page. Sourced from ONE global Dropbox folder (same for every
 * owner). Manual scrolling only: arrow buttons + mouse drag + native
 * touch/trackpad scroll with snap. Clicking a card plays the video in an
 * in-portal modal (Range-streamed same-origin URL — iPhone-safe).
 *
 * Self-hiding: renders nothing until loaded, and nothing if the folder is
 * empty/missing. Accepts the page `styles` object for card/title consistency.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { useIsNarrowViewport } from '../../utils/useIsNarrowViewport.js';
import {
  getOwnerVideoGuidesList,
  getOwnerVideoGuidesFileUrl,
  getOwnerVideoGuidesThumbnailUrl,
} from '../../services/ownerApi.js';

const VIDEO_EXTS = new Set(['mp4', 'mov', 'm4v', 'webm']);
const ACCENT = '#254860';
const isVideoName = (n) => VIDEO_EXTS.has(String(n).split('.').pop().toLowerCase());
// "03 Understanding Payouts.mp4" -> "Understanding Payouts"
const cleanTitle = (name) => String(name).replace(/\.[^.]+$/, '').replace(/^\d+[\s._-]+/, '').trim() || name;

function VideoPosterCover({ url, title }) {
  const [failed, setFailed] = useState(!url);
  return (
    <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${ACCENT}, #14202b)` }}>
      {!failed && (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {/* dark scrim + play badge */}
      <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, rgba(15,23,42,0) 45%, rgba(15,23,42,0.35))' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.94)', boxShadow: '0 3px 12px rgba(0,0,0,0.3)' }}>
          <Play size={22} style={{ color: ACCENT, marginLeft: 3 }} fill={ACCENT} />
        </span>
      </span>
      {failed && (
        <span style={{ position: 'absolute', left: 0, right: 0, bottom: 10, textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', padding: '0 0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
      )}
    </div>
  );
}

export default function VideoGuidesCarousel({ styles }) {
  const isNarrow = useIsNarrowViewport();
  const [videos, setVideos] = useState(null); // null = loading, [] = none
  const [active, setActive] = useState(null); // { title, url }
  const [nav, setNav] = useState({ left: false, right: false, frac: 0, visible: 1 });
  const scrollerRef = useRef(null);
  const dragRef = useRef({ down: false, startX: 0, startScroll: 0, moved: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getOwnerVideoGuidesList();
        const vids = (res?.entries || [])
          .filter((e) => e.kind === 'file' && isVideoName(e.name))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
        if (alive) setVideos(vids);
      } catch {
        if (alive) setVideos([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const updateNav = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setNav({
      left: el.scrollLeft > 4,
      right: el.scrollLeft < max - 4,
      frac: max > 0 ? Math.min(1, Math.max(0, el.scrollLeft / max)) : 0,
      visible: el.scrollWidth > 0 ? Math.min(1, el.clientWidth / el.scrollWidth) : 1,
    });
  }, []);

  useEffect(() => {
    if (!videos || videos.length === 0) return undefined;
    updateNav();
    const onResize = () => updateNav();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [videos, updateNav]);

  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(300, el.clientWidth * 0.8);
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  // Mouse drag-to-scroll (touch/trackpad use native scrolling).
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse') return;
    const el = scrollerRef.current;
    if (!el) return;
    dragRef.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: 0 };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.down) return;
    const el = scrollerRef.current;
    if (!el) return;
    const dx = e.clientX - d.startX;
    d.moved = Math.max(d.moved, Math.abs(dx));
    el.scrollLeft = d.startScroll - dx;
  };
  const endDrag = () => { dragRef.current.down = false; };

  const openVideo = (v) => {
    if (dragRef.current.moved > 5) { dragRef.current.moved = 0; return; } // was a drag, not a click
    dragRef.current.moved = 0;
    setActive({ title: cleanTitle(v.name), url: getOwnerVideoGuidesFileUrl(v.rel_path) });
  };

  if (!videos || videos.length === 0) return null;

  // Clean circular arrows in a control bar BELOW the carousel (not over the cards).
  const navBtn = (dir) => {
    const enabled = dir < 0 ? nav.left : nav.right;
    return (
      <button
        type="button"
        aria-label={dir < 0 ? 'Scroll left' : 'Scroll right'}
        onClick={() => scrollByDir(dir)}
        className="ovg-arrow"
        disabled={!enabled}
        style={{
          boxSizing: 'border-box', flex: '0 0 44px', padding: 0, lineHeight: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 44, height: 44, minWidth: 44, minHeight: 44,
          borderRadius: '50%', border: `1.5px solid ${enabled ? ACCENT : '#dbe2ea'}`,
          background: '#fff', color: enabled ? ACCENT : '#c2cbd6',
          cursor: enabled ? 'pointer' : 'default', boxShadow: '0 1px 3px rgba(16,24,40,0.10)',
        }}
      >
        {dir < 0 ? <ChevronLeft size={22} strokeWidth={2.5} /> : <ChevronRight size={22} strokeWidth={2.5} />}
      </button>
    );
  };

  return (
    <div style={styles.reportsCard}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem' }}>
        <h2 style={styles.sectionTitle}>Owner Portal Video Guides</h2>
        <span style={{ fontSize: '0.82rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{videos.length} videos · swipe or use arrows</span>
      </div>
      <div style={{ position: 'relative' }}>
        <div
          ref={scrollerRef}
          className="ovg-scroller"
          onScroll={updateNav}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          style={{
            display: 'flex', gap: '1rem', overflowX: 'auto', scrollSnapType: 'x mandatory',
            padding: '0.35rem 0.15rem 0.6rem', scrollBehavior: 'smooth',
          }}
        >
          {videos.map((v, i) => (
            <div
              key={v.rel_path}
              className="olrp-help-card"
              onClick={() => openVideo(v)}
              style={{
                flex: '0 0 auto', width: isNarrow ? '78%' : '272px', scrollSnapAlign: 'start',
                border: '1px solid #e7ebf0', borderRadius: '14px', background: '#fff', overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(16,24,40,0.04)', cursor: 'pointer', userSelect: 'none',
                transition: 'transform .18s cubic-bezier(.4,0,.2,1), box-shadow .18s, border-color .18s',
              }}
            >
              <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: ACCENT }}>
                <VideoPosterCover url={getOwnerVideoGuidesThumbnailUrl(v.rel_path)} title={cleanTitle(v.name)} />
                <span style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em', color: '#fff', background: 'rgba(15,23,42,0.55)', borderRadius: '6px', padding: '0.15rem 0.4rem' }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <div style={{ padding: '0.75rem 0.9rem 0.9rem' }}>
                <div title={cleanTitle(v.name)} style={{
                  fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', lineHeight: 1.3,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.35em',
                }}>{cleanTitle(v.name)}</div>
                <div style={{ marginTop: '0.4rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', fontWeight: 600, color: ACCENT }}>
                  <Play size={13} fill={ACCENT} /> Watch
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Control bar BELOW the carousel: arrows flanking a live progress track */}
      {nav.visible < 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.1rem' }}>
          {navBtn(-1)}
          <div style={{ position: 'relative', width: '220px', maxWidth: '45vw', height: '4px', borderRadius: '999px', background: '#e7ebf0' }}>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, borderRadius: '999px', background: ACCENT,
              width: `${Math.max(14, nav.visible * 100)}%`,
              left: `${nav.frac * (100 - Math.max(14, nav.visible * 100))}%`,
              transition: 'left .08s linear',
            }} />
          </div>
          {navBtn(1)}
        </div>
      )}

      {active && createPortal((
        <div
          role="dialog" aria-modal="true"
          onClick={() => setActive(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(15,23,42,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isNarrow ? 0 : '2rem' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#0b1620', borderRadius: isNarrow ? 0 : '12px', width: isNarrow ? '100%' : 'min(1000px, 95vw)', height: isNarrow ? '100%' : 'auto', maxHeight: isNarrow ? '100%' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', color: '#e2e8f0', background: '#0f1c28' }}>
              <span style={{ flex: 1, fontWeight: 600, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{active.title}</span>
              <button type="button" onClick={() => setActive(null)} title="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'inline-flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
              <video src={active.url} controls autoPlay playsInline preload="metadata" style={{ width: '100%', maxWidth: '100%', maxHeight: isNarrow ? '100%' : '80vh' }} />
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
